/**
 * Geocoding Cache
 * Caches geocoding results with different TTLs based on location type
 * 
 * Cache Duration Strategy:
 * - Permanent/Very Long (1+ years): Landmarks, airports, cities, popular POIs
 * - Medium-Term (1-6 months): Addresses, businesses, street names
 * - Short-Term (< 1 week): "Near me" queries, temporary locations, ambiguous queries
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import type { GeocodeResult } from '@/services/maps';

const CACHE_KEY = '@tripflow_geocode_results';
const CACHE_VERSION_KEY = '@tripflow_geocode_cache_version';
/** Flag set when user clears geocode cache so map can force re-geocode on next load. */
export const GEOCODE_CACHE_CLEARED_KEY = '@tripflow_geocode_cache_cleared';
const CURRENT_CACHE_VERSION = '7'; // Bump: country-aware destination geocoding + bbox validation
const MAX_CACHE_SIZE = 1000; // LRU cap for predictable memory and persistence size

// Cache expiry times in milliseconds
const PERMANENT_TTL = 365 * 24 * 60 * 60 * 1000; // 1 year (effectively permanent)
const MEDIUM_TTL = 6 * 30 * 24 * 60 * 60 * 1000; // 6 months
const SHORT_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days

type LocationType = 'permanent' | 'medium' | 'short';

export type GeocodeCacheOptions = {
  country?: string;
  proximity?: { latitude: number; longitude: number };
  /** When set, cached coordinates farther than this from `proximity` are treated as a miss. */
  maxDistanceKm?: number;
};

type CachedGeocodeResult = {
  data: GeocodeResult;
  cachedAt: number;
  lastAccessedAt: number; // For LRU eviction
  locationType: LocationType;
  address: string; // Original address for reference
};

type GeocodeCache = {
  [key: string]: CachedGeocodeResult;
};

/**
 * Determine location type based on address characteristics
 */
const determineLocationType = (address: string, result?: GeocodeResult): LocationType => {
  const normalizedAddress = address.toLowerCase().trim();
  
  // Permanent locations - landmarks, airports, cities, POIs
  const permanentPatterns = [
    // Landmarks and monuments
    /\b(eiffel tower|statue of liberty|big ben|taj mahal|colosseum|pyramids|great wall)\b/i,
    // Airports (IATA codes or airport keywords)
    /\b(airport|aeroport|flughafen|aeropuerto)\b/i,
    /\b([A-Z]{3})\s+(airport|international|terminal)\b/i, // IATA codes
    // Train stations
    /\b(central station|gare|hauptbahnhof|estaci[oó]n central|train station)\b/i,
    // Cities and countries (standalone or with "in")
    /^(paris|tokyo|new york|london|rome|berlin|madrid|amsterdam|vienna|prague|barcelona|istanbul|dubai|singapore|hong kong|sydney|melbourne|toronto|vancouver|montreal|miami|los angeles|san francisco|chicago|boston|seattle|portland|austin|denver|atlanta|philadelphia|washington|moscow|beijing|shanghai|mumbai|delhi|bangkok|seoul|taipei|osaka|kyoto|florence|venice|milan|naples|lisbon|porto|athens|stockholm|copenhagen|oslo|helsinki|dublin|edinburgh|glasgow|manchester|birmingham|brussels|zurich|geneva|munich|frankfurt|hamburg|cologne|stuttgart|dresden|hannover|nuremberg|rotterdam|the hague|antwerp|ghent|bruges|lyon|marseille|nice|toulouse|bordeaux|strasbourg|lille|nantes|rennes|toulon|grenoble|dijon|angers|saint-[eé]tienne|le havre|reims|montpellier|caen|nîmes|saint-denis|versailles|tours|limoges|amiens|perpignan|metz|besan[çc]on|boulogne|orl[ée]ans|mulhouse|rouen|argenteuil|saint-denis|montreuil|nancy|roubaix|tourcoing|nanterre|avignon|cr[ée]teil|dunkirk|poitiers|asni[èe]res|versailles|courbevoie|vitry|aulnay|colombes|la rochelle|champigny|rueil|antony|saint-maur|bourgogne|neuilly|villejuif|issy|noisy|sevran|cannes|calais|m[ée]rignac|beziers|bourges|castres|ch[âa]lons|chalon|ch[âa]teauroux|cholet|draguignan|evreux|foix|gap|laval|m[âa]con|mamers|melun|montauban|mont-de-marsan|moulins|narbonne|niort|pamiers|p[ée]rigueux|poitiers|privas|saint-brieuc|saint-l[ôo]|tarbes|thionville|troyes|valence|vesoul|vichy|vienne|vitr[ée])\b/i,
    // Museums and monuments
    /\b(museum|mus[ée]e|museo|museu|gallery|galerie|monument|memorial|cathedral|basilica|palace|ch[âa]teau|castle|fortress|temple|shrine|mosque|church|abbey|monastery)\b/i,
    // Parks and natural landmarks
    /\b(national park|parc national|nationalpark|parque nacional|central park|hyde park|yosemite|yellowstone|grand canyon|niagara falls|mount|mt\.|volcano|volcan|mountain|montagne|monta[ñn]a|berg|fjord|lake|lac|lago|river|fleuve|rio|sea|mer|mar|ocean|oc[ée]an)\b/i,
  ];
  
  // Check if it matches permanent patterns
  for (const pattern of permanentPatterns) {
    if (pattern.test(normalizedAddress)) {
      return 'permanent';
    }
  }
  
  // Check result context for permanent indicators
  if (result?.context) {
    const contextText = result.context.map(c => c.text.toLowerCase()).join(' ');
    const permanentContextPatterns = [
      /\b(airport|airport code|iata)\b/i,
      /\b(landmark|monument|heritage|unesco)\b/i,
      /\b(city|country|region|state|province)\b/i,
    ];
    
    for (const pattern of permanentContextPatterns) {
      if (pattern.test(contextText)) {
        return 'permanent';
      }
    }
  }
  
  // Short-term locations - "near me", ambiguous queries, temporary
  const shortTermPatterns = [
    /\b(near me|nearby|close to me|around here|current location|my location)\b/i,
    /\b(main street|high street|first street|second street|park avenue|oak street|elm street)\b/i, // Common ambiguous street names
    /\b(construction|pop-up|temporary|event|festival|fair|market)\b/i,
    /\b(restaurant|restaurant near|cafe|caf[ée]|bar|pub|club|nightclub)\b/i, // Can close/move
  ];
  
  for (const pattern of shortTermPatterns) {
    if (pattern.test(normalizedAddress)) {
      return 'short';
    }
  }
  
  // Default to medium-term for addresses, businesses, etc.
  return 'medium';
};

/**
 * Get TTL for location type
 */
const getTTL = (locationType: LocationType): number => {
  switch (locationType) {
    case 'permanent':
      return PERMANENT_TTL;
    case 'medium':
      return MEDIUM_TTL;
    case 'short':
      return SHORT_TTL;
  }
};

/**
 * Generate cache key from address and optional options (country, rounded proximity).
 * Same address with different country/proximity gets different entries to improve hit rate.
 */
const generateCacheKey = (address: string, options?: GeocodeCacheOptions): string => {
  const base = address.toLowerCase().trim().replace(/\s+/g, ' ');
  if (!options) return base;
  const parts: string[] = [base];
  if (options.country) {
    parts.push(`country=${options.country.toLowerCase()}`);
  }
  if (options.proximity != null) {
    // Round to 2 decimals so nearby views share the same key
    const lat = Math.round(options.proximity.latitude * 100) / 100;
    const lng = Math.round(options.proximity.longitude * 100) / 100;
    parts.push(`proximity=${lat},${lng}`);
  }
  if (options.maxDistanceKm != null && Number.isFinite(options.maxDistanceKm)) {
    parts.push(`maxd=${Math.round(options.maxDistanceKm)}`);
  }
  return parts.join('|');
};

/**
 * Check and migrate cache version
 */
const checkCacheVersion = async (): Promise<boolean> => {
  try {
    const storedVersion = await AsyncStorage.getItem(CACHE_VERSION_KEY);
    if (storedVersion !== CURRENT_CACHE_VERSION) {
      // Cache version mismatch - clear old cache
      console.log(`[GeocodeCache] Cache version changed from ${storedVersion} to ${CURRENT_CACHE_VERSION}, clearing cache`);
      await clearGeocodeCache();
      await AsyncStorage.setItem(CACHE_VERSION_KEY, CURRENT_CACHE_VERSION);
      return false;
    }
    return true;
  } catch (error) {
    console.warn('[GeocodeCache] Error checking cache version:', error);
    return false;
  }
};

/**
 * Get cached geocode result. Optionally pass options so the cache key matches request context (country, proximity).
 */
export const getCachedGeocode = async (
  address: string,
  options?: GeocodeCacheOptions
): Promise<GeocodeResult | null> => {
  try {
    const versionValid = await checkCacheVersion();
    if (!versionValid) return null;

    const cacheData = await AsyncStorage.getItem(CACHE_KEY);
    if (!cacheData) return null;

    const cache: GeocodeCache = JSON.parse(cacheData);
    const key = generateCacheKey(address, options);
    const cached = cache[key];

    if (!cached) return null;

    const age = Date.now() - cached.cachedAt;
    const ttl = getTTL(cached.locationType);
    if (age > ttl) {
      delete cache[key];
      await AsyncStorage.setItem(CACHE_KEY, JSON.stringify(cache));
      return null;
    }

    const now = Date.now();
    cached.lastAccessedAt = now;
    await AsyncStorage.setItem(CACHE_KEY, JSON.stringify(cache));

    return cached.data;
  } catch (error) {
    console.warn('[GeocodeCache] Error reading cache:', error);
    return null;
  }
};

/**
 * Cache geocode result. Pass same options used for lookup so the key is consistent.
 */
export const cacheGeocode = async (
  address: string,
  result: GeocodeResult,
  options?: GeocodeCacheOptions
): Promise<void> => {
  try {
    const cacheData = await AsyncStorage.getItem(CACHE_KEY);
    const cache: GeocodeCache = cacheData ? JSON.parse(cacheData) : {};
    const now = Date.now();

    const key = generateCacheKey(address, options);
    const locationType = determineLocationType(address, result);

    cache[key] = {
      data: result,
      cachedAt: now,
      lastAccessedAt: now,
      locationType,
      address,
    };

    const entries = Object.keys(cache);
    if (entries.length > MAX_CACHE_SIZE) {
      // LRU eviction: remove entries with oldest lastAccessedAt; prefer keeping permanent
      const sorted = entries.sort((a, b) => {
        const aEntry = cache[a];
        const bEntry = cache[b];
        const aAccess = aEntry.lastAccessedAt ?? aEntry.cachedAt;
        const bAccess = bEntry.lastAccessedAt ?? bEntry.cachedAt;
        if (aEntry.locationType === 'permanent' && bEntry.locationType !== 'permanent') return 1;
        if (bEntry.locationType === 'permanent' && aEntry.locationType !== 'permanent') return -1;
        return aAccess - bAccess;
      });
      const toRemove = sorted.slice(0, entries.length - MAX_CACHE_SIZE);
      toRemove.forEach((k) => delete cache[k]);
    }

    await AsyncStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.warn('[GeocodeCache] Error caching geocode:', error);
  }
};

/**
 * Clear geocode cache
 */
export const clearGeocodeCache = async (): Promise<void> => {
  try {
    await AsyncStorage.removeItem(CACHE_KEY);
  } catch (error) {
    console.warn('[GeocodeCache] Error clearing cache:', error);
  }
};

/**
 * Set a flag so the map knows to re-geocode pins after cache was cleared (e.g. from Settings).
 * Call this after clearGeocodeCache() when the user explicitly clears the geocoding cache.
 */
export const setGeocodeCacheClearedFlag = async (): Promise<void> => {
  try {
    await AsyncStorage.setItem(GEOCODE_CACHE_CLEARED_KEY, '1');
  } catch (error) {
    console.warn('[GeocodeCache] Error setting cache-cleared flag:', error);
  }
};

/**
 * Check and clear the "geocode cache was cleared" flag.
 * Returns true if the flag was set (map should refresh pins / re-geocode).
 */
export const getAndClearGeocodeCacheClearedFlag = async (): Promise<boolean> => {
  try {
    const value = await AsyncStorage.getItem(GEOCODE_CACHE_CLEARED_KEY);
    if (value) {
      await AsyncStorage.removeItem(GEOCODE_CACHE_CLEARED_KEY);
      return true;
    }
    return false;
  } catch (error) {
    console.warn('[GeocodeCache] Error reading cache-cleared flag:', error);
    return false;
  }
};

/**
 * Remove a specific entry from the geocode cache
 */
export const removeCachedGeocode = async (address: string): Promise<void> => {
  try {
    const cacheData = await AsyncStorage.getItem(CACHE_KEY);
    if (!cacheData) return;

    const cache: GeocodeCache = JSON.parse(cacheData);
    const baseKey = address.toLowerCase().trim().replace(/\s+/g, ' ');
    const keysToDelete = Object.keys(cache).filter(
      (key) => key === baseKey || key.startsWith(`${baseKey}|`),
    );

    if (keysToDelete.length > 0) {
      keysToDelete.forEach((key) => {
        delete cache[key];
      });
      await AsyncStorage.setItem(CACHE_KEY, JSON.stringify(cache));
      if (__DEV__) {
        console.log(
          `[GeocodeCache] Removed ${keysToDelete.length} cache entr${keysToDelete.length === 1 ? 'y' : 'ies'} for: ${address}`,
        );
      }
    }
  } catch (error) {
    console.warn('[GeocodeCache] Error removing cache entry:', error);
  }
};

/**
 * Clear geocode cache and return success status
 * Useful for console debugging: window.clearGeocodeCache()
 */
export const clearGeocodeCacheWithLog = async (): Promise<boolean> => {
  try {
    await clearGeocodeCache();
    console.log('[GeocodeCache] ✅ Cache cleared successfully');
    return true;
  } catch (error) {
    console.error('[GeocodeCache] ❌ Failed to clear cache:', error);
    return false;
  }
};

/**
 * Get all cached geocode entries (for debugging)
 * Useful for console debugging: window.getGeocodeCache()
 */
export const getAllCachedGeocodes = async (): Promise<Array<{ address: string; result: GeocodeResult; cachedAt: number }>> => {
  try {
    const cacheData = await AsyncStorage.getItem(CACHE_KEY);
    if (!cacheData) return [];

    const cache: GeocodeCache = JSON.parse(cacheData);
    return Object.entries(cache).map(([key, value]) => ({
      address: value.address || key,
      result: value.data,
      cachedAt: value.cachedAt,
    }));
  } catch (error) {
    console.warn('[GeocodeCache] Error getting all cached geocodes:', error);
    return [];
  }
};

/**
 * Get cache statistics (for debugging)
 */
export const getCacheStats = async (): Promise<{
  total: number;
  permanent: number;
  medium: number;
  short: number;
  oldest: number;
  newest: number;
}> => {
  try {
    const cacheData = await AsyncStorage.getItem(CACHE_KEY);
    if (!cacheData) {
      return { total: 0, permanent: 0, medium: 0, short: 0, oldest: 0, newest: 0 };
    }

    const cache: GeocodeCache = JSON.parse(cacheData);
    const entries = Object.values(cache);
    
    const stats = {
      total: entries.length,
      permanent: entries.filter(e => e.locationType === 'permanent').length,
      medium: entries.filter(e => e.locationType === 'medium').length,
      short: entries.filter(e => e.locationType === 'short').length,
      oldest: entries.length > 0 ? Math.min(...entries.map(e => e.cachedAt)) : 0,
      newest: entries.length > 0 ? Math.max(...entries.map(e => e.cachedAt)) : 0,
    };

    return stats;
  } catch (error) {
    console.warn('[GeocodeCache] Error getting cache stats:', error);
    return { total: 0, permanent: 0, medium: 0, short: 0, oldest: 0, newest: 0 };
  }
};

// --- Map pins persistence (per trip) so "return to app" shows pins without re-geocoding ---
const MAP_PINS_PREFIX = '@tripflow_map_pins_';
const MAP_PINS_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/** Serializable pin for persistence (no large `data`). */
export type PersistedMapPin = {
  id: string;
  tripId?: string | null;
  coordinates: { latitude: number; longitude: number };
  title: string;
  type: 'itinerary' | 'landmark' | 'search' | 'user';
  category?: string;
  dateKey?: string | null;
  cityIndex?: number | null;
  locationText?: string | null;
};

export async function getPersistedMapPins(tripId: string): Promise<PersistedMapPin[] | null> {
  try {
    const raw = await AsyncStorage.getItem(MAP_PINS_PREFIX + tripId);
    if (!raw) return null;
    const { pins, savedAt } = JSON.parse(raw) as { pins: PersistedMapPin[]; savedAt: number };
    if (!Array.isArray(pins) || pins.length === 0) return null;
    if (Date.now() - savedAt > MAP_PINS_MAX_AGE_MS) return null;
    return pins;
  } catch {
    return null;
  }
}

export async function setPersistedMapPins(
  tripId: string,
  pins: Array<{ id: string; tripId?: string | null; coordinates: { latitude: number; longitude: number }; title: string; type: string; category?: unknown; dateKey?: string | null; cityIndex?: number | null; locationText?: string | null }>,
): Promise<void> {
  try {
    const toPersist: PersistedMapPin[] = pins.map((p) => ({
      id: p.id,
      tripId: p.tripId ?? null,
      coordinates: p.coordinates,
      title: p.title,
      type: p.type as PersistedMapPin['type'],
      category: p.category as string | undefined,
      dateKey: p.dateKey ?? null,
      cityIndex: p.cityIndex ?? null,
      locationText: p.locationText ?? null,
    }));
    await AsyncStorage.setItem(
      MAP_PINS_PREFIX + tripId,
      JSON.stringify({ pins: toPersist, savedAt: Date.now() }),
    );
  } catch (e) {
    if (__DEV__) console.warn('[GeocodeCache] setPersistedMapPins failed', e);
  }
}

/** Remove persisted map pin keys (e.g. on sign-out so the next account cannot read them). */
export async function clearAllPersistedMapPinsKeys(): Promise<void> {
  try {
    const keys = await AsyncStorage.getAllKeys();
    const toRemove = keys.filter((k) => k.startsWith(MAP_PINS_PREFIX));
    if (toRemove.length > 0) {
      await AsyncStorage.multiRemove(toRemove);
    }
  } catch {
    /* best-effort */
  }
}

